import * as React from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { pageBounds, pageRange } from 'lib/pageRange';
import { Button } from './button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

/**
 * One pagination language for every list:
 * - `TablePager`   tables: "1–10 / 142", numbered pages, page size, page jump on long lists
 * - `CompactPager` narrow places (side panels, small cards): "1–10 / 142  ‹ 1 / 15 ›"
 * - `LoadMore`     card grids and phone lists
 * The current page is a lime fill (primary); everything else stays neutral.
 */

/** Past this many pages the pager also offers a "go to page" field. */
const JUMP_THRESHOLD = 10;

const slotClass =
  'inline-flex h-8 min-w-8 items-center justify-center rounded-md px-1.5 text-[13px] font-medium tabular-nums transition-colors disabled:pointer-events-none';

interface PagerArrowProps {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
  className?: string;
}

function PagerArrow({ direction, disabled, onClick, className }: PagerArrowProps) {
  const { t } = useTranslation();
  const Icon = direction === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={t(direction === 'prev' ? 'go_to_previous_page' : 'go_to_next_page')}
      className={cn(slotClass, 'w-8 border border-border bg-card text-foreground/80 hover:bg-muted disabled:text-muted-foreground/40', className)}
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
    </button>
  );
}

function RangeLabel({ from, to, total, noun }: { from: number; to: number; total: number; noun?: string }) {
  const { t } = useTranslation();
  return (
    <p className="whitespace-nowrap text-muted-foreground tabular-nums">
      <span className="font-medium text-foreground">
        {from}–{to}
      </span>{' '}
      {t('of')} {total}
      {noun ? ` ${noun}` : ''}
    </p>
  );
}

interface PageJumpProps {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

function PageJump({ page, pageCount, onPageChange }: PageJumpProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState(String(page));

  React.useEffect(() => setDraft(String(page)), [page]);

  const commit = () => {
    const wanted = Number.parseInt(draft, 10);
    if (Number.isNaN(wanted)) {
      setDraft(String(page));
      return;
    }
    const next = Math.min(Math.max(1, wanted), pageCount);
    setDraft(String(next));
    if (next !== page) onPageChange(next);
  };

  return (
    <label className="flex items-center gap-2 whitespace-nowrap text-muted-foreground">
      {t('page')}
      <input
        type="text"
        inputMode="numeric"
        aria-label={t('pager_go_to_page')}
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
        onBlur={commit}
        className="h-8 w-11 rounded-md border border-input bg-card px-1 text-center text-[13px] tabular-nums text-foreground focus-visible:outline-none"
      />
      <span className="tabular-nums">
        {t('of')} {pageCount}
      </span>
    </label>
  );
}

export interface TablePagerProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  pageSize: number;
  /** Total number of items across all pages. */
  total: number;
  onPageChange: (page: number) => void;
  /** Omit to hide the page-size control. */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  /** Already-translated noun after the total, e.g. "ta test". */
  noun?: string;
  className?: string;
}

/** Table footer pager: range on the left; page jump, numbered pages and page size on the right. */
export function TablePager({
  page,
  pageCount,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  noun,
  className,
}: TablePagerProps) {
  const { t } = useTranslation();
  if (total <= 0) return null;

  const pages = Math.max(1, pageCount);
  const current = Math.min(Math.max(1, page), pages);
  const { from, to } = pageBounds(current, pageSize, total);

  return (
    <nav
      aria-label={t('pagination')}
      className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-[13px]', className)}
    >
      <RangeLabel from={from} to={to} total={total} noun={noun} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {pages > JUMP_THRESHOLD && <PageJump page={current} pageCount={pages} onPageChange={onPageChange} />}
        {pages > 1 && (
          <div className="flex items-center gap-1">
            <PagerArrow direction="prev" disabled={current <= 1} onClick={() => onPageChange(current - 1)} />
            {pageRange(current, pages).map((item, index) =>
              item === 'gap' ? (
                <span key={`gap-${index}`} aria-hidden className="w-6 text-center text-muted-foreground">
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => item !== current && onPageChange(item)}
                  aria-current={item === current ? 'page' : undefined}
                  aria-label={t('pager_page_n', { page: item })}
                  className={cn(
                    slotClass,
                    item === current
                      ? 'bg-primary font-semibold text-primary-foreground'
                      : 'text-foreground/80 hover:bg-muted hover:text-foreground',
                  )}
                >
                  {item}
                </button>
              ),
            )}
            <PagerArrow direction="next" disabled={current >= pages} onClick={() => onPageChange(current + 1)} />
          </div>
        )}
        {onPageSizeChange && (
          <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
            <SelectTrigger aria-label={t('per_page')} className="h-8 w-auto gap-1.5 px-2.5 text-[13px]">
              <SelectValue>{t('pager_per_page', { size: pageSize })}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {t('pager_per_page', { size })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </nav>
  );
}

export interface CompactPagerProps {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
}

/** Pager for narrow places: the range, then ‹ current / total ›. */
export function CompactPager({ page, pageCount, pageSize, total, onPageChange, className }: CompactPagerProps) {
  const { t } = useTranslation();
  if (total <= 0) return null;

  const pages = Math.max(1, pageCount);
  const current = Math.min(Math.max(1, page), pages);
  const { from, to } = pageBounds(current, pageSize, total);

  return (
    <nav aria-label={t('pagination')} className={cn('flex items-center justify-between gap-3 text-[13px]', className)}>
      <RangeLabel from={from} to={to} total={total} />
      {pages > 1 && (
        <div className="flex items-center gap-2">
          <PagerArrow direction="prev" disabled={current <= 1} onClick={() => onPageChange(current - 1)} className="h-9 w-9" />
          <span className="min-w-[52px] text-center tabular-nums text-foreground/80">
            {current} {t('of')} {pages}
          </span>
          <PagerArrow direction="next" disabled={current >= pages} onClick={() => onPageChange(current + 1)} className="h-9 w-9" />
        </div>
      )}
    </nav>
  );
}

export interface LoadMoreProps {
  /** Items currently on screen. */
  shown: number;
  total: number;
  /** How many the next load adds. */
  step: number;
  onLoadMore: () => void;
  loading?: boolean;
  /** Phones: one full-width button, "Ko'proq yuklash  10 / 142". */
  compact?: boolean;
  className?: string;
}

/** "12 / 26 shown" and a full-width button for the next batch; the button goes once everything is shown. */
export function LoadMore({ shown, total, step, onLoadMore, loading = false, compact = false, className }: LoadMoreProps) {
  const { t } = useTranslation();
  if (total <= 0) return null;

  const remaining = Math.max(0, total - shown);
  if (compact) {
    if (remaining === 0) {
      return (
        <p className={cn('text-center text-[13px] tabular-nums text-muted-foreground', className)} aria-live="polite">
          {t('load_more_shown', { shown: Math.min(shown, total), total })}
        </p>
      );
    }
    return (
      <Button
        variant="outline"
        size="lg"
        className={cn('h-11 w-full gap-2 px-4 text-[14px]', className)}
        onClick={onLoadMore}
        disabled={loading}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
        {t('load_more')}
        <span className="font-normal tabular-nums text-muted-foreground">
          {Math.min(shown, total)} / {total}
        </span>
      </Button>
    );
  }
  return (
    <div className={cn('flex flex-col items-center gap-2.5', className)}>
      <p className="text-[13px] text-muted-foreground tabular-nums" aria-live="polite">
        {t('load_more_shown', { shown: Math.min(shown, total), total })}
      </p>
      {remaining > 0 && (
        <Button variant="outline" size="lg" className="w-full max-w-sm px-4" onClick={onLoadMore} disabled={loading}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
          {t('load_more_step', { step: Math.min(step, remaining) })}
        </Button>
      )}
    </div>
  );
}
